import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ThemeProvider } from 'next-themes';
import '@/App.css';
import LoginPage from './pages/LoginPage';
import MagicLoginPage from './pages/MagicLoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Dashboard from './pages/Dashboard';
import PublicGallery from './pages/PublicGallery';
import SimpleGallery from './pages/SimpleGallery';
import FindMeGallery from './pages/FindMeGallery';
import CheckinPage from './pages/CheckinPage';
import HeadshotRecipientGallery from './pages/HeadshotRecipientGallery';
import BrandedGallery from './pages/BrandedGallery';
import ShowcasePage from './pages/ShowcasePage';
import PortfolioGallery from './pages/PortfolioGallery';
import GalleryUnavailable from './components/GalleryUnavailable';
import { isSharingBlocked, subscribeSharingBlocked } from './lib/sharingGate';
import SettingsPage from './pages/SettingsPage';
import GalleryAdmin from './components/admin/GalleryAdmin';
import { DirectUploadProvider } from './contexts/DirectUploadContext';
import { FloatingUploadProgress } from './components/FloatingUploadProgress';
import { Toaster } from '@/components/ui/sonner';

// Use environment variable if set, otherwise use current origin (for production)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Redirect component for old find-me URLs
const FindMeRedirect = () => {
  const { findMeToken } = useParams();
  return <Navigate to={`/findme/${findMeToken}`} replace />;
};

const UserStatsRedirect = () => {
  const { galleryId } = useParams();
  return <Navigate to={`/gallery/${galleryId}`} replace />;
};

// Wrapper component for GalleryAdmin that fetches galleries
const GalleryAdminWrapper = ({ token, backendUrl, role }) => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/galleries`, axiosConfig);
        setGalleries(response.data);
      } catch (error) {
        console.error('Error fetching galleries:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGalleries();
  }, [token, backendUrl]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#BF212F]"></div>
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-center" richColors />
      <GalleryAdmin
        axiosConfig={axiosConfig}
        backendUrl={backendUrl}
        galleries={galleries}
        userRole={role}
      />
    </>
  );
};

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [username, setUsername] = useState(localStorage.getItem('username'));
  const [role, setRole] = useState(localStorage.getItem('role') || 'admin');

  const handleLogin = (accessToken, user, userRole) => {
    const resolvedRole = userRole || 'admin';
    setToken(accessToken);
    setUsername(user);
    setRole(resolvedRole);
    localStorage.setItem('token', accessToken);
    localStorage.setItem('username', user);
    localStorage.setItem('role', resolvedRole);
  };

  const handleLogout = () => {
    setToken(null);
    setUsername(null);
    setRole('admin');
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
  };

  // Re-hydrate role/identity from the server on load (and drop stale tokens).
  useEffect(() => {
    if (!token) return;
    axios
      .get(`${BACKEND_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        if (data?.role) {
          setRole(data.role);
          localStorage.setItem('role', data.role);
        }
        if (data?.username) {
          setUsername(data.username);
          localStorage.setItem('username', data.username);
        }
      })
      .catch((err) => {
        if (err?.response?.status === 401) handleLogout();
      });
  }, [token]);

  const axiosConfig = {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };

  // Sharing switched off, caught in one place.
  //
  // Every client-facing gallery page is its own component with its own load
  // and its own error branch, and the backend closes all of them at once with
  // a 403 carrying `sharing_disabled`. Catching it once here means one screen
  // for all of them — including any viewer added later — instead of six copies
  // of the same branch, each free to drift into showing "Gallery not found"
  // for a link that is merely paused. The interceptor itself lives in
  // lib/sharingGate at module scope; see the note there for why it cannot be
  // registered from this effect. Admin routes are never gated, so this cannot
  // fire while someone is working in the admin.
  const [sharingBlocked, setSharingBlocked] = useState(isSharingBlocked);
  useEffect(() => subscribeSharingBlocked(setSharingBlocked), []);

  if (sharingBlocked) return <GalleryUnavailable />;

  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
    <BrowserRouter>
      <DirectUploadProvider>
        <Routes>
          {/* Public gallery routes */}
          <Route path="/share/:shareToken" element={<PublicGallery />} />
          <Route path="/share/:shareToken/c/:collectionSlug" element={<PublicGallery />} />
          <Route path="/simple/:simpleToken" element={<SimpleGallery />} />
          <Route path="/findme/:findMeToken" element={<FindMeGallery />} />
          <Route path="/checkin/:slug" element={<CheckinPage />} />
          <Route path="/headshot/:accessToken" element={<HeadshotRecipientGallery />} />
          <Route path="/branded/:brandedToken" element={<BrandedGallery />} />
          <Route path="/branded/:brandedToken/c/:collectionSlug" element={<BrandedGallery />} />

          {/* Redirect old find-me URLs (with dash) to findme (no dash) for backwards compatibility */}
          <Route path="/find-me/:findMeToken" element={<FindMeRedirect />} />
          
          {/* Portfolio/Showcase routes */}
          <Route path="/showcase" element={<ShowcasePage />} />
          <Route path="/portfolio/:galleryId" element={<PortfolioGallery />} />
          
          {/* Password reset routes */}
          <Route 
            path="/forgot-password" 
            element={
              token ? <Navigate to="/" replace /> : <ForgotPasswordPage />
            } 
          />
          <Route 
            path="/reset-password" 
            element={
              token ? <Navigate to="/" replace /> : <ResetPasswordPage />
            } 
          />
          
          {/* Settings route */}
          <Route 
            path="/settings" 
            element={
              token ? <SettingsPage /> : <Navigate to="/login" replace />
            } 
          />
          
          {/* Old User Stats page — now the People section of the gallery's Analytics panel */}
          <Route path="/gallery/:galleryId/user-stats" element={<UserStatsRedirect />} />
          
          {/* Gallery Admin route - full screen layout */}
          <Route
            path="/gallery/:galleryId"
            element={
              token ? (
                <GalleryAdminWrapper
                  token={token}
                  backendUrl={BACKEND_URL}
                  role={role}
                />
              ) : <Navigate to="/login" replace />
            }
          />

          <Route
            path="/login"
            element={
              token ? <Navigate to="/" replace /> : <LoginPage onLogin={handleLogin} />
            }
          />
          {/* Crew passwordless magic-link login */}
          <Route
            path="/crew/:magicToken"
            element={<MagicLoginPage onLogin={handleLogin} backendUrl={BACKEND_URL} />}
          />
          <Route
            path="/*"
            element={
              token ? (
                <Dashboard
                  token={token}
                  username={username}
                  role={role}
                  onLogout={handleLogout}
                  backendUrl={BACKEND_URL}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
        {/* Only show upload progress on authenticated pages */}
        {token && <FloatingUploadProgress />}
      </DirectUploadProvider>
    </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
